import React, { useEffect, useState } from "react";

// material-ui
import { Box, Button, Dialog, Divider, IconButton, Typography } from "@mui/material";

// assets
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNone from "@mui/icons-material/NotificationsNone";
import MailOutline from "@mui/icons-material/MailOutline";
import SecurityOutlined from "@mui/icons-material/SecurityOutlined";
import ChevronRight from "@mui/icons-material/ChevronRight";

// project imports
import useAuth from "hooks/useAuth";

// ==============================|| SUBSCRIPTION EXPIRY REMINDER ||============================== //

// localStorage key that tracks which reminders were already shown. Kept separate
// from the Onboarding dialog keys ('setopendialog'/'logcount') and the quote popup.
const STORAGE_KEY = "subscriptionReminder";
const CONTACT_EMAIL = "connect@vivekmashrani.com";
const MAILTO_HREF = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Subscription Renewal")}`;

// design tokens per the Membership Renewal Reminder spec
const C = {
  primary: "#1E4FD8",
  primaryHover: "#1740B0",
  navy: "#1A2B4C",
  orange: "#E8862E",
  badgeBg: "#E8F0FE",
  bannerBg: "#EAF1FE",
  panelBg: "#F7F9FC",
  body: "#333333",
  border: "#E4E9F2",
  muted: "#9AA4B5",
};

// ---------- pure date helpers (exported for easy sanity-testing) ----------

// Local calendar date of a Date object as 'YYYY-MM-DD'
export const toDateOnly = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseDateOnly = (str) => {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Whole calendar days from today to the end date (negative when past)
export const daysBetween = (endStr, todayStr) => {
  if (!endStr || !todayStr) return null;
  const ms = parseDateOnly(endStr) - parseDateOnly(todayStr);
  return Math.round(ms / 86400000);
};

export const defaultReminderState = { expiry: "", month: false, fifteen: false, daily: "" };

/**
 * Decide which reminder (if any) should show right now. All reminders fire
 * BEFORE expiry (the server login gate already blocks users once the
 * subscription has expired, so nothing shows after):
 *  - exactly 30 days before expiry -> 'month' reminder (once, only on that exact day)
 *  - exactly 15 days before expiry -> 'fifteen' reminder (once, only on that exact day)
 *  - 0..10 days before expiry      -> 'daily' reminder (every login / app open)
 * Other days within the last month show nothing until the daily window begins.
 * When the stored state belongs to an older expiry date (renewed/extended
 * subscription), milestone flags are reset so reminders can fire again.
 * Returns null, or { variant, daysLeft, state } where `state` is the persisted flags to save.
 */
export const decideReminder = (endStr, todayStr, stored = defaultReminderState) => {
  const daysLeft = daysBetween(endStr, todayStr);
  if (daysLeft === null) return null;

  // fresh (or renewed) subscription -> forget previous milestone flags
  let state = { ...defaultReminderState, ...stored };
  if (state.expiry !== endStr) {
    state = { expiry: endStr, month: false, fifteen: false, daily: "" };
  }

  // after the expiry date there is no reminder (expired users are blocked at login)
  if (daysLeft < 0) return null;

  // 0..10 days before expiry (incl. the expiry day): remind on every login/app open
  if (daysLeft <= 10) {
    return { variant: "daily", daysLeft, state: { ...state, daily: todayStr } };
  }

  // Milestone reminders fire once each, and only on their exact due day:
  // exactly 30 days left ('month') and exactly 15 days left ('fifteen').
  if (daysLeft === 30) {
    if (state.month) return null;
    return { variant: "month", daysLeft, state: { ...state, month: true } };
  }
  if (daysLeft === 15) {
    if (state.fifteen) return null;
    return { variant: "fifteen", daysLeft, state: { ...state, fifteen: true } };
  }

  // any other day (or more than a month out) -> nothing to show
  return null;
};

// ---------- dialog content helpers ----------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const formatDateLabel = (endStr) => {
  const [y, m, d] = String(endStr).split("-").map(Number);
  if (!y || !m || !d) return endStr;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/**
 * Build the display content (calendar + copy) for a reminder variant.
 * All variants share the same card layout; only the title, orange status
 * label and headline sentence adapt. headlineBefore/headlineAfter are spliced
 * around the highlighted (bold blue) date.
 */
const buildContent = (variant, daysLeft, endStr) => {
  const date = new Date(`${endStr}T00:00:00`);
  const isExpiryDay = daysLeft === 0;
  const base = {
    endStr,
    dateLabel: formatDateLabel(endStr),
    monthYear: `${MONTHS[date.getMonth()]} ${date.getFullYear()}`,
    weekday: WEEKDAYS[date.getDay()].toUpperCase(),
    day: String(date.getDate()).padStart(2, "0"),
    paraBefore: "To continue enjoying Quantam Mastermind community benefits without interruption, please ",
    paraLink: "renew your subscription",
    paraAfter: ".",
  };

  if (isExpiryDay) {
    return {
      ...base,
      title: "Subscription Expiring Soon",
      calendarStatus: "Expires today",
      headlineBefore: "Your subscription expires today (",
      headlineAfter: ").",
    };
  }
  return {
    ...base,
    title: variant === "fifteen" ? "Subscription Expiring Soon" : "Membership Renewal Reminder",
    calendarStatus: `${daysLeft} days left`,
    headlineBefore: "Your subscription will expire on ",
    headlineAfter: ` (${daysLeft} days remaining).`,
  };
};

const SubscriptionReminder = () => {
  const { Profiledata } = useAuth();
  // null = nothing to show; otherwise { variant, daysLeft, endStr }
  const [reminder, setReminder] = useState(null);

  const handleClose = () => setReminder(null);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const raw = window.localStorage.getItem("users");
        if (!raw) return;
        const { id } = JSON.parse(raw);
        if (!id) return;

        const res = await Profiledata(id);
        if (!res || res.status !== "200") return;
        const profile = res.data || {};
        if (!profile.SubscriptionEnd) return;

        const endStr = String(profile.SubscriptionEnd).slice(0, 10);
        const todayStr = toDateOnly(new Date());

        let stored = {};
        try {
          stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
        } catch (e) {
          stored = {};
        }

        const decision = decideReminder(endStr, todayStr, stored);
        if (!decision) return;

        // Persist immediately so the once-only milestone flags ('month'/'fifteen')
        // are not lost on a reload. 'daily' no longer suppresses repeat showings.
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(decision.state));

        setReminder({ variant: decision.variant, daysLeft: decision.daysLeft, endStr });
      } catch (err) {
        console.error("Subscription reminder check failed:", err);
      }
    };
    checkSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = reminder ? buildContent(reminder.variant, reminder.daysLeft, reminder.endStr) : null;

  return (
    <Dialog
      open={Boolean(reminder)}
      onClose={handleClose}
      aria-labelledby="subscription-reminder-title"
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 900,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "20px",
          boxShadow: "0 20px 50px rgba(26, 43, 76, 0.18)",
          background: "#ffffff",
          fontFamily: "'Poppins', 'Roboto', sans-serif",
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
        },
      }}
    >
      {content && (
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          {/* ---------- HEADER ---------- */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 3 }, flexWrap: "wrap", mt : '-10rem' }}>
            {/* bell icon badge */}
            <Box
              sx={{
                position: "relative",
                width: { xs: 76, sm: 90 },
                height: { xs: 76, sm: 90 },
                borderRadius: "50%",
                bgcolor: C.badgeBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <NotificationsNone sx={{ fontSize: { xs: 40, sm: 46 }, color: C.primary }} />
                {/* orange alert badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -8,
                    minWidth: { xs: 20, sm: 24 },
                    height: { xs: 20, sm: 24 },
                    borderRadius: "50%",
                    bgcolor: C.orange,
                    border: "2px solid #ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.12)",
                  }}
                >
                  <Typography sx={{ color: "#fff", fontSize: { xs: 12, sm: 14 }, fontWeight: 700, lineHeight: 1 }}>
                    !
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* title */}
            <Typography
              id="subscription-reminder-title"
              sx={{ flex: 1, minWidth: 200, fontSize: { xs: 22, sm: 28, md: 32 }, fontWeight: 700, color: C.navy, lineHeight: 1.2 }}
            >
              {content.title}
            </Typography>

            {/* close */}
            <IconButton
              onClick={handleClose}
              aria-label="close"
              sx={{
                width: 40,
                height: 40,
                border: "1px solid #E0E5EE",
                bgcolor: "#ffffff",
                color: "#8A94A6",
                "&:hover": { bgcolor: C.panelBg, color: C.navy },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* ---------- BODY PANEL ---------- */}
          <Box
            sx={{
              mt: 3,
              bgcolor: C.panelBg,
              borderRadius: "12px",
              p: { xs: 2, sm: 3 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "stretch",
              gap: { xs: 2, md: 3 },
            }}
          >
            {/* calendar card */}
            <Box
              sx={{
                width: { xs: "100%", md: 230 },
                flexShrink: 0,
                borderRadius: "10px",
                overflow: "hidden",
                border: `1px solid ${C.border}`,
                bgcolor: "#ffffff",
                boxShadow: "0 2px 10px rgba(26, 43, 76, 0.08)",
                mx: { xs: "auto", md: 0 },
                maxWidth: { xs: 260, md: 230 },
              }}
            >
              <Box sx={{ bgcolor: C.primary, py: 1, textAlign: "center" }}>
                <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, letterSpacing: 0.5 }}>{content.monthYear}</Typography>
              </Box>
              <Box sx={{ px: 2, pb: 2.5, textAlign: "center" }}>
                <Typography sx={{ color: C.muted, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, mt: 1.5 }}>{content.weekday}</Typography>
                <Typography sx={{ color: C.navy, fontSize: { xs: 40, md: 48 }, fontWeight: 700, lineHeight: 1.1 }}>{content.day}</Typography>
                <Typography sx={{ color: C.orange, fontWeight: 700, fontSize: 14, mt: 0.5 }}>{content.calendarStatus}</Typography>
              </Box>
            </Box>

            {/* vertical divider (desktop) */}
            <Divider orientation="vertical" flexItem sx={{ borderColor: C.border, display: { xs: "none", md: "block" } }} />
            {/* horizontal divider (mobile) */}
            <Divider sx={{ borderColor: C.border, display: { xs: "block", md: "none" } }} />

            {/* text column */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 500, color: C.body, lineHeight: 1.5 }}>
                {content.headlineBefore}
                <Box component="span" sx={{ color: C.primary, fontWeight: 700 }}>
                  {content.dateLabel}
                </Box>
                {content.headlineAfter}
              </Typography>

              <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: C.body, lineHeight: 1.5, mt: 1.5 }}>
                {content.paraBefore}
                {/* <Box component="a" href={MAILTO_HREF} onClick={handleClose} sx={{ color: C.primary, fontWeight: 700, textDecoration: "none", cursor: "pointer" }}>
                  {content.paraLink}
                </Box> */}
                  {content.paraLink}
                {content.paraAfter}
              </Typography>

              {/* contact row */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mt: 2.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: C.badgeBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MailOutline sx={{ color: C.primary, fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: C.body, fontSize: { xs: 15, md: 16 }, lineHeight: 1.4 }}>For renewal assistance, contact</Typography>
                  <Box component="a" href={MAILTO_HREF} onClick={handleClose} sx={{ color: C.primary, fontWeight: 700, fontSize: { xs: 15, md: 16 }, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                    {CONTACT_EMAIL}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ---------- INFO STRIP ---------- */}
          <Box sx={{ mt: 2.5, bgcolor: C.bannerBg, borderRadius: "12px", px: { xs: 2, sm: 3 }, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SecurityOutlined sx={{ color: C.primary, fontSize: 20 }} />
            </Box>
            <Typography sx={{ color: C.navy, fontSize: { xs: 15, md: 16 }, fontWeight: 500, lineHeight: 1.5 }}>
              Stay connected. Renew today to keep enjoying all community benefits.
            </Typography>
          </Box>

          {/* ---------- FOOTER ---------- */}
          {/* <Box sx={{ mt: 3 }}>
            <Divider sx={{ borderColor: "#E7EBF3" }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3, flexWrap: "wrap" }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  px: "28px",
                  py: "14px",
                  color: C.navy,
                  borderColor: "#C7CFDC",
                  bgcolor: "#ffffff",
                  fontWeight: 600,
                  fontSize: 16,
                  textTransform: "none",
                  "&:hover": { borderColor: C.muted, bgcolor: C.panelBg },
                }}
              >
                Remind Me Later
              </Button>
              <Button
                component="a"
                href={MAILTO_HREF}
                onClick={handleClose}
                variant="contained"
                disableElevation
                sx={{
                  borderRadius: "8px",
                  px: "28px",
                  py: "14px",
                  bgcolor: C.primary,
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "none",
                  boxShadow: "0 6px 16px rgba(30, 79, 216, 0.28)",
                  "&:hover": { bgcolor: C.primaryHover },
                }}
              >
                Renew Now
                <ChevronRight sx={{ ml: 0.5 }} />
              </Button>
            </Box>
          </Box> */}
        </Box>
      )}
    </Dialog>
  );
};

export default SubscriptionReminder;
